<template>
  <div class="mt-3">
    <eg-b-card class="mt-3 py-1">
      <eg-stepper
        v-model="loading"
        finish-label="Grade"
        :steps-titles="stepsTitles"
        @beforeChange="beforeChange"
        @finish="finishHandler"
      >
        <template #slot-1>
          <import-select-class
            :id="serviceId"
            v-model="selectedClassIds"
            :bulk="true"
            :platform-key="platformKey"
            @loaded="loading = false"
          />
        </template>
        <template #slot-2>
          <import-select-assignment
            :id="serviceId"
            v-model="selectedAssignments"
            :platform-key="platformKey"
            :class-ids="selectedClassIds"
            :bulk="true"
            @loaded="loading = false"
            @setAssignmentDefaultRubricId="setAssignmentDefaultRubricIdHandler"
          />
        </template>
        <template #slot-4>
          <import-bulk-select-rubric
            ref="importBulkSelectRubric"
            v-model="selectedAssignments"
          />
        </template>
      </eg-stepper>
    </eg-b-card>
  </div>
</template>

<script>
import { getSingleRubric } from "@/api";
import importApi from "@/api/requests/import";
import EgStepper from "@/components/global/eg_stepper.vue";
import ImportSelectClass from "./imports_select_class.vue";
import ImportSelectAssignment from "./imports_select_assignment.vue";
import ImportBulkSelectRubric from "./imports_bulk_select_rubric.vue";
import errorHandler from "./errorHandler";
import { LMS_PROVIDERS } from "@/constants";

export default {
  name: "ImportWizardBulk",

  components: {
    EgStepper,
    ImportSelectClass,
    ImportSelectAssignment,
    ImportBulkSelectRubric,
  },

  inject: ["serviceAccount", "serviceName", "workspacesProvider"],

  props: {
    platform: {
      type: String,
      required: true,
      validate: (value) => Object.keys(LMS_PROVIDERS).includes(value),
    },
    serviceId: {
      type: String,
      default: "",
    },
    platformKey: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      LMS_PROVIDERS,
      loading: false,
      selectedClassIds: [],
      selectedAssignments: [],
      selectedSubmissionsIds: [],
      selectedRubric: null,
      selectedLanguageId: null,
      rubricIntensity: "moderate",
      gradeLevel: "elementary",
      draftAssignmentId: null,
    };
  },

  computed: {
    stepsTitles() {
      return [
        "Step one: Select a class",
        "Step two: Select assignment",
        "Step three: Select rubric",
      ];
    },
  },

  methods: {
    async setAssignmentDefaultRubricIdHandler(rubricIds) {
      if (rubricIds.length === 0) return;

      const { data } = await getSingleRubric(rubricIds[0]);
      this.selectedRubric = data;
    },

    async beforeChange({ index, resolve, reject }) {
      switch (index) {
        case 0: {
          if (this.selectedClassIds.length === 0) {
            reject();
            this.$showToastError("Please select a class", {
              title: "Validation Error",
            });
          } else {
            resolve();
          }
          break;
        }
        case 1:
          if (this.selectedAssignments.length === 0) {
            reject();
            this.$showToastError("Please select an assignment", {
              title: "Validation Error",
            });
          } else {
            resolve();
          }
          break;
        case 2: {
          const isDone = await this.$refs.importBulkSelectRubric.validate();

          if (isDone) {
            resolve();
          } else {
            reject();
            this.$showToastError("Please select a rubric for each asignments", {
              title: "Validation Error",
            });
          }
          break;
        }
        case 3: {
          const isDone = await this.$refs.importSelectRubric.validate();

          if (isDone) {
            resolve();
          } else {
            reject();
            this.$showToastError("Please select a rubric", {
              title: "Validation Error",
            });
          }

          break;
        }

        case 4: {
          const isDone = await this.$refs.importSelectDetails.validate();

          if (isDone) {
            resolve();
          } else {
            reject();
            this.$showToastError(
              "There was an error updating the google assignment, please try again.",
              {
                title: "Validation Error",
              }
            );
          }

          break;
        }

        default:
          resolve();
      }
    },

    async finishHandler(resolve) {
      const payload = {
        tasks: this.selectedAssignments.map((assignment) => ({
          classId: assignment.courseId,
          assignmentId: assignment.id,
          rubricId: assignment.rubric.id,
        })),
      };

      try {
        const result = await importApi[this.serviceName].bulkImport(
          this.serviceAccount.id,
          payload
        );
        this.$showToastSuccess(
          `Import successful! Your submissions are now being graded.`,
          { title: "Import successful" }
        );
        await this.workspacesProvider.init();
        resolve();
        // TODO: redirect to the first created class
        this.$router.push({
          name: "workspace",
          params: {
            workspaceKey: "canvas",
            id: result.data[0].egAssignmentLevelFolder.id,
          },
        });
        return;
      } catch (error) {
        errorHandler({
          vm: this,
          error,
          title: "Import Error",
          message:
            "There was a problem importing submissions from the classroom. Please try again.",
        });
        return resolve();
      }
    },
  },
};
</script>
